import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import type { SxProps, Theme } from '@mui/material/styles';

import { getGroupSx, labelSx, type SegmentedControlWidth } from './styles';

export interface SegmentButton {
  /** Visible segment label. */
  name: string;
  /** Value reported to `onClickButton` when the segment is chosen. */
  value: string | number;
  isDisabled?: boolean;
}

export interface SegmentedControlProps {
  buttons: Array<SegmentButton>;
  /** Value of the currently selected segment (null = none selected). */
  selectedButton?: string | number | null;
  onClickButton: (value: string | number) => void;
  /** 'full' segments share the width equally; 'inline' hugs content. */
  width?: SegmentedControlWidth;
  label?: string;
  isDisabled?: boolean;
  testId?: string;
  /** Applied to the outer wrapper (e.g. flex sizing inside a filter bar). */
  sx?: SxProps<Theme>;
}

/**
 * Reusable segmented control supporting 2+ segments, built on MUI's
 * `ToggleButtonGroup` (exclusive selection) so it matches the design-system /
 * prototype look. The parent owns the selected state: clicking a segment fires
 * `onClickButton(value)`; re-clicking the active segment is ignored so a
 * selection is always retained.
 */
const SegmentedControl: React.FC<SegmentedControlProps> = ({
  buttons,
  selectedButton = null,
  onClickButton,
  width = 'inline',
  label,
  isDisabled = false,
  testId,
  sx,
}) => (
  <Box data-testid={testId} sx={sx}>
    {label && (
      <Typography
        component="span"
        sx={labelSx}
        data-testid="SegmentedControl|Label"
      >
        {label}
      </Typography>
    )}
    <ToggleButtonGroup
      exclusive
      size="small"
      value={selectedButton}
      disabled={isDisabled}
      aria-label={label}
      data-testid="SegmentedControl|Group"
      onChange={(_, value: string | number | null) => {
        if (value !== null) {
          onClickButton(value);
        }
      }}
      sx={getGroupSx(width)}
    >
      {buttons.map((button) => (
        <ToggleButton
          key={button.value}
          value={button.value}
          disabled={button.isDisabled}
          data-testid="SegmentedControl|Button"
        >
          {button.name}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  </Box>
);

export default SegmentedControl;
